import { Button } from "@/components/ui/baseUi/button"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@/components/ui/baseUi/combobox"
import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { displayedFullDateFormat } from "@/modules/utils/dateUtils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import { type SimpleFormik } from "@/typings/formik"
import worldImage from "@images/svgIcons/worldIcon.svg"
import { type ProfileRegisterFormikType, type ProfileRegisterInputKey } from "@modules/auth/profileRegisterFormikTypes"
import { word } from "@modules/core/utils/i18n"
import { useCountriesListArray } from "@modules/language/utils/countries"
import dayjs from "dayjs"
import { ArrowLeft } from "lucide-react"
import { useMemo } from "react"

export type RegisterFormThreePageProps = {
  handleSubmit: () => void
  onPrevious: () => void
  formik: SimpleFormik<ProfileRegisterFormikType, ProfileRegisterInputKey>
  getFormErrorMessage: (name: ProfileRegisterInputKey, errorMessage?: string) => React.JSX.Element | undefined
  disabled: boolean
  className?: string
} & React.ComponentProps<"div">

export function RegisterFormThreePage({
  handleSubmit,
  onPrevious,
  formik,
  getFormErrorMessage,
  disabled,
  className,
}: RegisterFormThreePageProps) {
  const countryOptions = useCountriesListArray()
  const countryItems = useMemo(
    () => countryOptions.map(({ code, label }) => ({ value: code, label })),
    [countryOptions],
  )
  const selectedCountry = countryItems.find((item) => item.value === formik.values.nationality) ?? null

  return (
    <div className={cn("flex grow flex-col justify-between gap-4", className)}>
      <div className="flex grow flex-col justify-between gap-3">
        <Button variant="ghost" size="xs" onClick={onPrevious} className="w-fit justify-start">
          <ArrowLeft />
          {word("global.previous")}
        </Button>

        <img src={worldImage} width={100} className="absolute top-[-12%] right-0" />
        <Text fontStyle="TM">3/4</Text>
        <Text fontStyle="TM">{word("auth.signup.form.page.3.title")}</Text>
        <Text fontStyle="BS" color="--neutral-700">
          {word("auth.signup.form.page.3.description")}
        </Text>

        {/* BIRTHDATE */}
        <CalendarInput
          name="birthdate"
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("birthdate")}
          placeholder={word("auth.signup.form.dateOfBirth")}
          calendarProps={{
            maxDate: dayjs().toDate(),
            minDate: dayjs().subtract(100, "year").toDate(),
            dateFormat: displayedFullDateFormat,
            captionLayout: "dropdown",
          }}
        />

        {/* NATIONALITY */}
        <Combobox
          items={countryItems}
          value={selectedCountry}
          onValueChange={(item) => void formik.setFieldValue("nationality", item?.value)}
          isItemEqualToValue={(a, b) => a.value === b.value}
          filter={(item, query) =>
            removeDiacritics(item.label.toLowerCase()).includes(removeDiacritics(query.toLowerCase()))
          }
        >
          <ComboboxTrigger render={<Button variant="outline" className="h-11 w-full justify-between" />}>
            <ComboboxValue placeholder={word("profile.form.nationality.label")} />
          </ComboboxTrigger>
          <ComboboxContent>
            <ComboboxInput placeholder={word("global.searchCountry")} showTrigger={false} />
            <ComboboxEmpty>{word("global.searchCountryNotFound")}</ComboboxEmpty>
            <ComboboxList>
              {(item: { value: string; label: string }) => (
                <ComboboxItem key={item.value} value={item}>
                  {item.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>

      <Button type="button" size="lg" variant="primary" onClick={handleSubmit} disabled={disabled}>
        {word("global.next")}
      </Button>
    </div>
  )
}
